import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import GridOverlay from './GridOverlay';

const ArtActivitySection = ({ activity, artworkData, onPopupStateChange }) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({}); // Track which steps are expanded
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(3); // 3x3, 4x4, or 6x6
  const [isArtworkExpanded, setIsArtworkExpanded] = useState(false);
  const [showGridSizeSelector, setShowGridSizeSelector] = useState(false);
  const artworkImageRef = useRef(null);
  const expandedArtworkRef = useRef(null);
  const gridControlsRef = useRef(null);
  const gridSizeSelectorRef = useRef(null);

  // Handle click outside grid size selector dropdown to collapse it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showGridSizeSelector && gridSizeSelectorRef.current && !gridSizeSelectorRef.current.contains(event.target)) {
        // Don't close if clicking on the grid toggle button
        if (gridControlsRef.current && gridControlsRef.current.contains(event.target)) {
          return;
        }
        setShowGridSizeSelector(false);
      }
    };

    if (isPopupOpen && showGridSizeSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isPopupOpen, showGridSizeSelector]);

  if (!activity) return null;

  const toggleStep = (index) => {
    setExpandedSteps(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleTip = (tipIndex) => {
    setExpandedSteps(prev => ({
      ...prev,
      [tipIndex]: !prev[tipIndex]
    }));
  };

  const handleButtonClick = () => {
    setIsPopupOpen(true);
    if (onPopupStateChange) onPopupStateChange(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setIsArtworkExpanded(false);
    if (onPopupStateChange) onPopupStateChange(false);
  };

  const handleArtworkClick = () => {
    if (showGrid) {
      setIsArtworkExpanded(true);
    }
  };

  const handleCloseExpandedArtwork = () => {
    setIsArtworkExpanded(false);
  };

  return (
    <>
      {/* Activity Button */}
      <button
        onClick={handleButtonClick}
        className="w-full bg-white border-4 border-amber-200 rounded-2xl shadow-lg p-4 hover:bg-amber-50 hover:border-amber-300 transition-all duration-200 text-left"
      >
        <h3 className="text-lg font-bold text-amber-600 mb-2">Try This Activity</h3>
        <h4 className="text-md font-semibold text-gray-800 mb-2">{activity.title}</h4>
        <p className="text-sm text-gray-700 mb-3">{activity.description || activity.objective}</p>
        <div className="text-xs text-amber-600 font-medium">
          Click to open activity guide →
        </div>
      </button>

      {/* Activity Popup - Pop-out like Expanded View */}
      {isPopupOpen && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100000] p-2 md:p-4">
          <div className="relative w-[98vw] md:w-[95vw] h-[95vh] md:h-[85vh] max-w-6xl max-h-[95vh] md:max-h-[85vh] bg-white rounded-xl md:rounded-2xl shadow-2xl border-2 md:border-4 border-amber-200 flex flex-col overflow-hidden">
            {/* Close Button - Top Right */}
            <button
              onClick={handleClosePopup}
              className="absolute top-2 right-2 md:top-4 md:right-4 z-50 p-2 md:p-3 bg-white hover:bg-gray-50 rounded-lg md:rounded-xl shadow-xl transition-all duration-200 border-2 border-gray-300 hover:border-amber-400"
              title="Close Activity Guide"
              style={{ minWidth: '36px', minHeight: '36px' }}
            >
              <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Mobile: Fully Vertical Layout | Desktop: Side-by-side */}
            <div className="w-full p-2 md:p-6 flex flex-col md:flex-row flex-1 min-h-0 gap-4 md:gap-6 overflow-y-auto" style={{
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              maxHeight: typeof window !== 'undefined' && window.innerWidth < 768 ? 'calc(95vh - 80px)' : '100%'
            }}>
              {/* Artwork Section - Full Width on Mobile, 50% on Desktop */}
              <div className="flex-shrink-0 relative w-full md:w-1/2" style={{ maxWidth: '600px' }}>
                  <h3 className="text-base md:text-xl font-bold text-gray-900 mb-2 md:mb-4">Artwork Reference</h3>
                  <div className="bg-white rounded-lg md:rounded-2xl border-2 md:border-4 border-amber-200 shadow-lg overflow-hidden p-2 md:p-4 relative">
                    {artworkData && (
                      <>
                        <img 
                          ref={artworkImageRef}
                          src={artworkData.imageUrl}
                          alt={artworkData.title || 'Artwork'}
                          className={`w-full h-auto object-contain relative ${showGrid ? 'cursor-pointer' : ''}`}
                          onClick={handleArtworkClick}
                          style={{ 
                            maxHeight: typeof window !== 'undefined' && window.innerWidth < 768 ? '40vh' : '50vh',
                            maxWidth: '100%',
                            position: 'relative',
                            zIndex: 1
                          }}
                        />
                        {/* Grid Overlay */}
                        <GridOverlay 
                          containerRef={artworkImageRef}
                          enabled={showGrid}
                          gridSize={gridSize}
                        />
                      </>
                    )}
                    
                    {/* Expand Artwork Button - Top Right Corner */}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setIsArtworkExpanded(true);
                      }}
                      className="absolute top-2 right-2 md:top-6 md:right-6 z-50 px-2 py-2 md:px-3 md:py-2 rounded-lg shadow-lg transition-all duration-200 border-2 bg-white/90 hover:bg-white text-amber-700 border-amber-200"
                      title="Expand Artwork"
                      style={{ minWidth: '36px', minHeight: '36px' }}
                    >
                      <svg className="w-3.5 h-3.5 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                      </svg>
                    </button>

                    {/* Grid Controls - Top Left of Artwork */}
                    <div ref={gridControlsRef} className="absolute top-2 left-2 md:top-6 md:left-6 z-50 flex flex-col gap-1.5 md:gap-2">
                      {/* Toggle Grid Button */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newShowGrid = !showGrid;
                          setShowGrid(newShowGrid);
                          // Show grid size selector when grid is enabled
                          if (newShowGrid) {
                            setShowGridSizeSelector(true);
                          } else {
                            setShowGridSizeSelector(false);
                          }
                        }}
                        className={`px-2 py-2 md:px-3 md:py-2 rounded-lg shadow-lg transition-all duration-200 border-2 ${
                          showGrid 
                            ? 'bg-amber-400 hover:bg-amber-500 text-white border-amber-300' 
                            : 'bg-white/90 hover:bg-white text-amber-700 border-amber-200'
                        }`}
                        title={showGrid ? 'Hide Grid' : 'Show Grid'}
                        style={{ minWidth: '36px', minHeight: '36px' }}
                      >
                        <svg className="w-3.5 h-3.5 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                      </button>
                      
                      {/* Grid Size Selector - Only show when grid is enabled and dropdown is open */}
                      {showGrid && showGridSizeSelector && (
                        <div ref={gridSizeSelectorRef} className="bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border-2 border-amber-200 p-1.5 md:p-2 flex flex-col gap-0.5 md:gap-1">
                          <div className="text-[10px] md:text-xs font-semibold text-amber-700 mb-0.5 md:mb-1">Grid:</div>
                          {[3, 4, 6].map((size) => (
                            <button
                              key={size}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setGridSize(size);
                              }}
                              className={`px-1.5 py-0.5 md:px-2 md:py-1 rounded text-[10px] md:text-xs font-medium transition-all ${
                                gridSize === size
                                  ? 'bg-amber-400 text-white'
                                  : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                              }`}
                            >
                              {size}×{size}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
              </div>

              {/* Activity Guide Section - Full Width on Mobile, 50% on Desktop */}
              <div className="flex-1 w-full md:w-1/2 flex flex-col min-h-0" style={{
                minHeight: typeof window !== 'undefined' && window.innerWidth < 768 ? 'auto' : '0'
              }}>
                <div className="bg-amber-50 rounded-lg md:rounded-2xl border-2 md:border-4 border-amber-200 p-3 md:p-6 flex flex-col" style={{
                  minHeight: typeof window !== 'undefined' && window.innerWidth < 768 ? 'auto' : '100%'
                }}>
                  <h3 className="text-sm md:text-xl font-bold text-amber-600 mb-2 md:mb-4 flex-shrink-0">Activity Guide</h3>
                  
                  {/* Content - No nested scrolling on mobile, parent handles it */}
                  <div className="flex-1" style={{ 
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#f97316 #fef3c7'
                  }}>
                    <style>{`
                      .activity-guide-scroll::-webkit-scrollbar {
                        width: 8px;
                      }
                      .activity-guide-scroll::-webkit-scrollbar-track {
                        background: #fef3c7;
                        border-radius: 4px;
                      }
                      .activity-guide-scroll::-webkit-scrollbar-thumb {
                        background: #f97316;
                        border-radius: 4px;
                      }
                      .activity-guide-scroll::-webkit-scrollbar-thumb:hover {
                        background: #ea580c;
                      }
                    `}</style>
                    
                    <div className="space-y-1.5 md:space-y-4 pr-2">
                      <div className="pb-1 md:pb-0">
                        <h4 className="text-xs md:text-lg font-semibold text-gray-800 mb-0.5 md:mb-2">{activity.title}</h4>
                        <p className="text-xs md:text-base text-gray-700 leading-tight md:leading-normal">{activity.description || activity.objective}</p>
                      </div>
                      
                      {activity.materials && activity.materials.length > 0 && (
                        <div className="pb-1 md:pb-0">
                          <h5 className="text-xs md:text-md font-semibold text-gray-800 mb-0.5 md:mb-2">Materials:</h5>
                          <ul className="text-xs md:text-sm text-gray-700 list-disc list-inside space-y-0 md:space-y-1">
                            {activity.materials.map((material, idx) => (
                              <li key={idx} className="leading-tight">{material}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Few Key Tips Section - Now inside scrollable container */}
                      <div className="mt-2 md:mt-6">
                        <h5 className="text-xs md:text-lg font-semibold text-gray-800 mb-1.5 md:mb-4">Few Key tips to attempt this activity:</h5>
                        <div className="space-y-1 md:space-y-2">
                      {/* Standardized Tip 1 - Common for all activities */}
                      {(() => {
                        const tip1Text = "First, click the grid button (the 4 squares) at the top left of the artwork. Then draw the same grid on your paper or drawing book!";
                        const isExpanded = expandedSteps['tip1'];
                        const previewText = tip1Text.length > 60 ? tip1Text.substring(0, 60) + '...' : tip1Text;
                        return (
                          <div className="border border-amber-200 rounded-lg overflow-hidden bg-white" style={{ 
                            scrollSnapAlign: typeof window !== 'undefined' && window.innerWidth < 768 ? 'start' : 'none',
                            scrollSnapStop: typeof window !== 'undefined' && window.innerWidth < 768 ? 'always' : 'normal'
                          }}>
                            <button
                              onClick={() => toggleTip('tip1')}
                              className="w-full px-2 md:px-4 py-1.5 md:py-3 text-left flex items-center justify-between hover:bg-amber-50 transition-colors"
                            >
                              <div className="flex items-start gap-1.5 md:gap-3 flex-1 min-w-0">
                                <span className="bg-amber-500 text-white rounded-full w-5 h-5 md:w-7 md:h-7 flex items-center justify-center text-[10px] md:text-sm font-bold flex-shrink-0 mt-0.5">
                                  1
                                </span>
                                <span className="text-[11px] md:text-sm text-gray-800 font-medium flex-1 leading-relaxed break-words">
                                  {isExpanded ? tip1Text : previewText}
                                </span>
                              </div>
                              <svg
                                className={`w-3.5 h-3.5 md:w-5 md:h-5 text-amber-600 flex-shrink-0 ml-1 md:ml-2 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        );
                      })()}

                      {/* Standardized Tip 2 - Common for all activities */}
                      {(() => {
                        const tip2Text = "Look at each square in the grid. Draw what you see in each square, one at a time, in the same place on your paper!";
                        const isExpanded = expandedSteps['tip2'];
                        const previewText = tip2Text.length > 60 ? tip2Text.substring(0, 60) + '...' : tip2Text;
                        return (
                          <div className="border border-amber-200 rounded-lg overflow-hidden bg-white" style={{ 
                            scrollSnapAlign: typeof window !== 'undefined' && window.innerWidth < 768 ? 'start' : 'none',
                            scrollSnapStop: typeof window !== 'undefined' && window.innerWidth < 768 ? 'always' : 'normal'
                          }}>
                            <button
                              onClick={() => toggleTip('tip2')}
                              className="w-full px-2 md:px-4 py-1.5 md:py-3 text-left flex items-center justify-between hover:bg-amber-50 transition-colors"
                            >
                              <div className="flex items-start gap-1.5 md:gap-3 flex-1 min-w-0">
                                <span className="bg-amber-500 text-white rounded-full w-5 h-5 md:w-7 md:h-7 flex items-center justify-center text-[10px] md:text-sm font-bold flex-shrink-0 mt-0.5">
                                  2
                                </span>
                                <span className="text-[11px] md:text-sm text-gray-800 font-medium flex-1 leading-relaxed break-words">
                                  {isExpanded ? tip2Text : previewText}
                                </span>
                              </div>
                              <svg
                                className={`w-3.5 h-3.5 md:w-5 md:h-5 text-amber-600 flex-shrink-0 ml-1 md:ml-2 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        );
                      })()}

                      {/* OpenAI Tips - Dynamic steps from activity */}
                      {activity.steps && activity.steps.length > 0 && activity.steps.map((step, idx) => {
                        const isExpanded = expandedSteps[`step_${idx}`];
                        // Truncate step text for preview (show first 60 chars on mobile, 80 on desktop)
                        const previewText = step.length > 60 ? step.substring(0, 60) + '...' : step;
                        return (
                          <div key={idx} className="border border-amber-200 rounded-lg overflow-hidden bg-white" style={{ 
                            scrollSnapAlign: typeof window !== 'undefined' && window.innerWidth < 768 ? 'start' : 'none',
                            scrollSnapStop: typeof window !== 'undefined' && window.innerWidth < 768 ? 'always' : 'normal'
                          }}>
                            <button
                              onClick={() => toggleTip(`step_${idx}`)}
                              className="w-full px-2 md:px-4 py-1.5 md:py-3 text-left flex items-center justify-between hover:bg-amber-50 transition-colors"
                            >
                              <div className="flex items-start gap-1.5 md:gap-3 flex-1 min-w-0">
                                <span className="bg-amber-500 text-white rounded-full w-5 h-5 md:w-7 md:h-7 flex items-center justify-center text-[10px] md:text-sm font-bold flex-shrink-0 mt-0.5">
                                  {idx + 3}
                                </span>
                                <span className="text-[11px] md:text-sm text-gray-800 font-medium flex-1 leading-relaxed break-words">
                                  {isExpanded ? step : previewText}
                                </span>
                              </div>
                              <svg
                                className={`w-3.5 h-3.5 md:w-5 md:h-5 text-amber-600 flex-shrink-0 ml-1 md:ml-2 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Expanded Artwork View - Full Screen with Grid */}
      {isArtworkExpanded && typeof document !== 'undefined' && artworkData && createPortal(
        <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center z-[100001] p-4">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close Button - Top Right */}
            <button
              onClick={handleCloseExpandedArtwork}
              className="absolute top-2 right-2 md:top-4 md:right-4 z-50 p-2 md:p-3 bg-white hover:bg-gray-50 rounded-lg md:rounded-xl shadow-xl transition-all duration-200 border-2 border-gray-300 hover:border-amber-400"
              title="Close Expanded View"
              style={{ minWidth: '36px', minHeight: '36px' }}
            >
              <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>

            {/* Expanded Artwork Container */}
            <div className="relative w-full h-full flex items-center justify-center p-4 md:p-8">
              <div ref={expandedArtworkRef} className="relative max-w-[98vw] md:max-w-[95vw] max-h-[98vh] md:max-h-[95vh]">
                <img 
                  src={artworkData.imageUrl}
                  alt={artworkData.title || 'Artwork'}
                  className="w-full h-auto object-contain"
                  style={{ 
                    maxHeight: '95vh',
                    maxWidth: '95vw'
                  }}
                />
                {/* Grid Overlay on Expanded View */}
                {showGrid && (
                  <GridOverlay 
                    containerRef={expandedArtworkRef}
                    enabled={showGrid}
                    gridSize={gridSize}
                  />
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ArtActivitySection;
